import { InfoOutlined, Square } from "@mui/icons-material";
import {
  Grid,
  Stack,
  Tooltip,
  TooltipProps,
  styled,
  tooltipClasses,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { getDateFormat } from "../../Constants/RoleAssignmentTable/Column";
import { Card, Divider, LoadingSpinner } from "cprime-foundational-components";
import Typography from "../Common/CprimeText";

export interface KpiCardSplit {
  status: string;
  info?: string;
  count: string;
  filter: Object;
  additional?: boolean;
}

export interface KpiCardprops {
  last_updated?: string;
  description?: string;
  label: string;
  count: string;
  refreshedDate?: string;
  handleClick?: (data: any) => any;
  splitUp?: KpiCardSplit[];
  additionalSplitup?: boolean;
}

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: "0px 0.802px 2.89px 0.802px rgba(0, 0, 0, 0.15)",
    fontSize: 11,
    maxWidth: "450px",
    minWidth: "300px",
    width: "min-content",
    padding: "8px 12px",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
    filter: "drop-shadow(0px 0.802px 2.89px rgba(0, 0, 0, 0.05))",
  },
}));

const KpiCard = ({
  kpiCards,
  callBack,
  isVehicle,
  style,
  isHoldView,
  metricsDetails,
  handleCardClick = (data) => {},
  isMock,
}: {
  kpiCards: KpiCardprops[];
  callBack?: Function;
  isVehicle?: boolean;
  style?: React.CSSProperties;
  isHoldView: boolean;
  metricsDetails?: any;
  handleCardClick?: (data: any) => any;
  isMock?: boolean;
}) => {
  const warningCancel =
    kpiCards?.[4]?.splitUp?.[0]?.status === "Cancel Hold In Progress" &&
    parseInt(kpiCards?.[4]?.splitUp?.[0]?.count) > 0;
  const warningOnHold =
    (kpiCards?.[2]?.splitUp?.[2]?.status === "PDC Conversion In Progress" &&
      parseInt(kpiCards?.[2]?.splitUp?.[2]?.count) > 0) ||
    (kpiCards?.[2]?.splitUp?.[0]?.status === "Hold In Progress" &&
      parseInt(kpiCards?.[2]?.splitUp?.[0]?.count) > 0);
  const warningCompleted =
    (kpiCards?.[3]?.splitUp?.[0]?.status === "Completion In Progress" &&
      parseInt(kpiCards?.[3]?.splitUp?.[0]?.count) > 0) ||
    metricsDetails?.okInProgress > 0 ||
    metricsDetails?.completionInProgress > 0 ||
    metricsDetails?.releaseInProgress > 0;
  const warningVehiclHold =
    (kpiCards?.[1]?.splitUp?.[0]?.status === "In Progress" ||
      kpiCards?.[1]?.splitUp?.[0]?.status === "Validating - Active Holds") &&
    parseInt(kpiCards?.[1]?.splitUp?.[0]?.count) > 0;

  const [showTooltip, setShowTooltip] = useState(true);

  // "Last refreshed on" should reflect when this page last received fresh
  // KPI data, not the API's own per-item last_updated field.
  const [lastRefreshedOn, setLastRefreshedOn] = useState(() => new Date().toISOString());

  useEffect(() => {
    setLastRefreshedOn(new Date().toISOString());
  }, [kpiCards]);

  const RenderToolTipText = ({
    item,
    index,
  }: {
    item: KpiCardprops;
    index: number;
  }) => {
    return (
      <Stack
        px={1}
        py={1}
        sx={{ flexDirection: "column" }}
        gap={1}
        data-testid="stack-close-tooltip-id"
        onClick={() => setShowTooltip(false)}
      >
        <Typography
          style={{
            fontFamily: "Toyota Type",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: "600",
            lineHeight: "16px",
          }}
        >
          {item.label}
        </Typography>
        <Typography
          variant="caption"
          style={{
            fontFamily: "Toyota Type",
            fontSize: "12px",
            fontStyle: "normal",
            fontWeight: "400",
            lineHeight: "18px",
          }}
        >
          Last refreshed on {getDateFormat(lastRefreshedOn)}
        </Typography>
        <Typography
          style={{
            fontFamily: "Toyota Type",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: "400",
            lineHeight: "normal",
          }}
        >
          {item?.description ?? "Description"}
        </Typography>
        {item?.splitUp && item?.splitUp?.length > 0 && (
          <>
            <br />
            <Divider />
            <>
              {item?.splitUp?.map((item, splitIdx) => {
                const loader =
                  item?.status === "Hold In Progress" ||
                  item?.status === "Completion In Progress" ||
                  item?.status === "Cancel Hold In Progress" ||
                  item?.status === "Validating - Active Holds";
                return (
                  <React.Fragment
                    key={`${item?.status ?? "split"}-${splitIdx}`}
                  >
                    <div
                      role="button"
                      data-testid="close-tooltip-id"
                      tabIndex={0}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                      }}
                      // onKeyDown={() => {}}
                      onClick={(evt) => {
                        if (callBack) {
                          setShowTooltip(false);
                          evt.stopPropagation();
                          callBack({ ...item.filter });
                        }
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "start",
                          width: "auto",
                          marginRight: "15px",
                        }}
                      >
                        {!loader ? (
                          <Square
                            fontSize="small"
                            sx={{
                              marginRight: "0.25rem",
                              color: item.additional ? "#E66C02" : "#58595B",
                              width: "9px",
                              height: "9px",
                            }}
                          />
                        ) : (
                          <LoadingSpinner
                            fullScreen={false}
                            // variant="in-line"
                            // sx={{
                            //   "&.MuiCircularProgress-root": {
                            //     width: "10px !important",
                            //     height: "10px !important",
                            //   },
                            //   margin: "0.3rem 0.2rem 0rem 0rem",
                            // }}
                          />
                        )}
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            width: "99%",
                          }}
                        >
                          <Typography variant="caption" className="kpiInfoText">
                            {item.status}
                          </Typography>
                          {item.info && (
                            <Typography
                              variant="caption"
                              className="kpiInfoText"
                            >
                              {item.info}
                            </Typography>
                          )}
                        </div>
                      </div>
                      <Typography variant="caption" className="kpiInfoCount">
                        {item.count + (isHoldView ? " Hold(s)" : " VIN(s)")}
                      </Typography>
                    </div>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </>
          </>
        )}
      </Stack>
    );
  };

  return (
    <>
    <Grid
      container
      spacing={2}
      data-testid={`CHM-KPICard`}
      sx={{ paddingBottom: "16px" }}
    >
      {kpiCards?.map((item: KpiCardprops, index) => (
        <Grid
          data-testid={`${isHoldView ? "holdView" : isVehicle ? "vehicle" : "general"}${item.label?.replace(
            /\s+/g,
            "",
          )}`}
          style={{ ...style }}
          item
          xs={12 / kpiCards?.length}
          lg={12 / kpiCards?.length}
          md={12 / kpiCards?.length}
          key={index}
        >
          <Card
            title={
              <div
                style={{
                  width: " 176px",
                  height: "10%",
                  flexShrink: "0",
                  borderRadius: "4px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  paddingBottom: "1rem",
                }}
              >
                <Stack
                  direction={"row"}
                  sx={{ alignItems: "flex-start" }}
                  gap={0.5}
                >
                  <Typography
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: "12px",
                      fontStyle: "normal",
                      fontWeight: "600",
                      lineHeight: "16px",
                    }}
                    data-testid={`CHM-KPICard-${item.label}`}
                  >
                    {item.label}
                  </Typography>
                  {showTooltip ? (
                    <>
                      <StyledTooltip
                        title={<RenderToolTipText item={item} index={index} />}
                        arrow={true}
                      >
                        <InfoOutlined fontSize="small" />
                      </StyledTooltip>
                      {isMock ? (
                        <RenderToolTipText item={item} index={index} />
                      ) : null}
                    </>
                  ) : (
                    <InfoOutlined
                      fontSize="small"
                      onMouseEnter={() => setShowTooltip(true)}
                    />
                  )}
                </Stack>
                {/* {item.additionalSplitup && <WarningOutlined sx={{ color: '#FF832B', height: '1.25rem' }} />} */}
                {(((warningOnHold || warningVehiclHold) &&
                  item.label === "Active Holds") ||
                  (warningCompleted && item.label === "Completed") ||
                  (warningCancel && item.label === "Canceled")) && (
                  <Typography
                    sx={{
                      fontFamily: "ToyotaType-Regular",
                      fontSize: "10px",
                    }}
                  >
                    Includes In-Progress Items
                  </Typography>
                )}
              </div>
            }
            onClick={() => handleCardClick(item)}
            variant={"clickable"}
            cardContent={
              <Typography
                fontSize={"32px"}
                fontStyle={"normal"}
                fontWeight={400}
                lineHeight={"40px"}
                paddingBottom={"0.6rem"}
                data-testid={`${
                  isHoldView
                    ? "holdViewSub"
                    : isVehicle
                      ? "vehicleSub"
                      : "generalSub"
                }${item.label?.replace(/\s+/g, "")}`}
              >
                {item.count}
              </Typography>
            }
            sx={{
              cursor: "pointer",
              boxShadow: "none",
              "& .MuiCardContent-root": {
                padding: "0 !important",
              },
              ...(((warningOnHold || warningVehiclHold) &&
                item.label === "Active Holds") ||
              (warningCompleted && item.label === "Completed") ||
              (warningCancel && item.label === "Canceled")
                ? {
                    "& .MuiCardHeader-root": {
                      paddingBottom: "0px !important",
                    },
                  }
                : {}),
            }}
          />
        </Grid>
      ))}
    </Grid>
    </>
  );
};

export default KpiCard;
